import { ethers } from 'ethers';
import api from './api';
import { BASE_CHAIN_PARAMS } from './chainParams';

const DEFAULT_BUFFER_ETH = 0.0001;
const POLL_MS = 800;
const POLL_ATTEMPTS = 10;

const RELAYER_OUT_OF_GAS_MESSAGE =
  'Relayer has no fund to drip gas. Get a little Base ETH to process transactions — they need a small amount of ETH to pay gas.';

const JACKPOT_MIN_MESSAGE =
  'Gas drip requires a minimum claimable Free jackpot balance. Accumulate more Free jackpot wins, or fund a little Base ETH yourself to claim smaller amounts.';

let relayerGasModalEl = null;

function getAuthUserId() {
  try {
    const token = localStorage.getItem('token');
    if (!token) return null;
    const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    return payload.userId ? String(payload.userId) : null;
  } catch {
    return null;
  }
}

function isRelayerOutOfGasError(e) {
  const code = e?.response?.data?.code;
  const msg = String(e?.response?.data?.message || e?.message || '').toLowerCase();
  return (
    code === 'RELAYER_OUT_OF_GAS' ||
    msg.includes('insufficient balance to drip') ||
    msg.includes('no fund to drip gas') ||
    msg.includes('relayer has insufficient')
  );
}

function isDripCooldownError(e) {
  const code = e?.response?.data?.code;
  return (
    code === 'FREE_DRIP_COOLDOWN' ||
    code === 'DRIP_COOLDOWN' ||
    code === 'DRIP_DAILY_CAP' ||
    code === 'DRIP_BUSY' ||
    code === 'DRIP_DISABLED' ||
    code === 'PRIMARY_WALLET_ONLY' ||
    code === 'JACKPOT_MIN_NOT_MET' ||
    code === 'ACCOUNT_TOO_NEW' ||
    code === 'USER_MISMATCH' ||
    code === 'RELAYER_PATH_DEPRECATED' ||
    e?.response?.status === 429
  );
}

function closeRelayerGasModal() {
  if (relayerGasModalEl?.parentNode) {
    relayerGasModalEl.parentNode.removeChild(relayerGasModalEl);
  }
  relayerGasModalEl = null;
  document.body.style.overflow = '';
}

function formatNextEligible(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  } catch {
    return String(iso);
  }
}

/**
 * Dismissible modal — relayer empty, cooldown, or min jackpot not met.
 */
export function showRelayerOutOfGasModal(message = RELAYER_OUT_OF_GAS_MESSAGE, opts = {}) {
  closeRelayerGasModal();

  const title = opts.title || 'Gas funding unavailable';
  const playType = normalizePlayType(opts.playType);
  const playTypeLabel = playType === 'free' ? 'Free jackpot' : playType === 'boost' ? 'Boost' : 'Market';
  const nextLine = opts.nextEligibleAt
    ? `<p class="text-sm font-semibold text-amber-700 dark:text-amber-300">Next ${playTypeLabel} drip available: ${formatNextEligible(opts.nextEligibleAt)}</p>`
    : '';
  const minLine =
    opts.minJackpotUsd != null
      ? `<p class="text-sm font-semibold text-amber-700 dark:text-amber-300">Minimum Free jackpot for gas drip: $${Number(opts.minJackpotUsd).toFixed(2)}${
          opts.claimableJackpotUsd != null
            ? ` (you have $${Number(opts.claimableJackpotUsd).toFixed(2)})`
            : ''
        }</p>`
      : '';

  const overlay = document.createElement('div');
  overlay.setAttribute('role', 'dialog');
  overlay.setAttribute('aria-modal', 'true');
  overlay.setAttribute('aria-labelledby', 'relayer-gas-modal-title');
  overlay.className =
    'fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4 sm:p-6';
  overlay.style.paddingTop = 'max(1rem, env(safe-area-inset-top))';
  overlay.style.paddingBottom = 'max(1rem, env(safe-area-inset-bottom))';

  overlay.innerHTML = `
    <div class="w-full max-w-md rounded-lg bg-white dark:bg-gray-800 shadow-xl overflow-hidden" data-relayer-gas-panel>
      <div class="flex items-center justify-between border-b border-gray-200 dark:border-gray-700 px-4 py-4 sm:px-6">
        <h2 id="relayer-gas-modal-title" class="text-lg font-bold text-gray-900 dark:text-white">
          ${String(title).replace(/</g, '&lt;')}
        </h2>
        <button type="button" data-relayer-gas-close aria-label="Close"
          class="rounded-lg p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-700 dark:hover:text-gray-300">
          <svg class="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
      <div class="px-4 py-4 sm:px-6 sm:py-5 space-y-3 text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
        <p>${String(message).replace(/</g, '&lt;')}</p>
        ${minLine}
        ${nextLine}
        <p class="text-xs text-gray-500 dark:text-gray-400">
          Bridge or buy a small amount of ETH on <strong>Base</strong>, then try again. Most actions need only a few cents of gas.
          ${
            playType === 'free'
              ? 'Or keep winning Free predictions until your claimable jackpot reaches the minimum for the Free jackpot gas drip.'
              : `The ${playTypeLabel} gas drip is separate from the Free jackpot minimum and follows the limits configured by the administrator.`
          }
        </p>
      </div>
      <div class="flex justify-end gap-2 border-t border-gray-200 dark:border-gray-700 px-4 py-3 sm:px-6">
        <button type="button" data-relayer-gas-close
          class="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-sm font-medium text-gray-800 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700">
          OK
        </button>
      </div>
    </div>
  `;

  const onClose = (e) => {
    if (e.target === overlay || e.target.closest('[data-relayer-gas-close]')) {
      closeRelayerGasModal();
    }
  };
  overlay.addEventListener('click', onClose);

  document.body.style.overflow = 'hidden';
  document.body.appendChild(overlay);
  relayerGasModalEl = overlay;
}

async function readEthBalance(address) {
  if (!address) return Number.NaN;
  if (typeof window !== 'undefined' && window.ethereum) {
    try {
      const provider = new ethers.BrowserProvider(window.ethereum);
      const network = await provider.getNetwork();
      if (Number(network.chainId) === Number(BASE_CHAIN_PARAMS.chainIdDecimal)) {
        const bal = await provider.getBalance(address);
        return parseFloat(ethers.formatEther(bal));
      }
    } catch {
      /* fall through to public RPCs */
    }
  }
  const urls = BASE_CHAIN_PARAMS.rpcUrls || [];
  for (let i = 0; i < urls.length; i += 1) {
    try {
      const provider = new ethers.JsonRpcProvider(urls[i], BASE_CHAIN_PARAMS.chainIdDecimal, {
        staticNetwork: true,
      });
      // eslint-disable-next-line no-await-in-loop
      const bal = await provider.getBalance(address);
      return parseFloat(ethers.formatEther(bal));
    } catch {
      /* try next RPC */
    }
  }
  // An RPC failure is not proof that the wallet has zero ETH. Treat it as
  // unknown so the backend can perform the authoritative balance check.
  return Number.NaN;
}

export const GAS_DRIP_BLOCKED = 'GAS_DRIP_BLOCKED';

export function isGasDripBlocked(error) {
  const code = error?.code;
  const msg = String(error?.message || '');
  return code === GAS_DRIP_BLOCKED || msg === GAS_DRIP_BLOCKED;
}

export function throwGasDripBlocked() {
  const err = new Error(GAS_DRIP_BLOCKED);
  err.code = GAS_DRIP_BLOCKED;
  throw err;
}

/**
 * True only when the wallet balance is known and covers the required gas.
 * An unknown (RPC-failed) balance is not treated as sufficient.
 */
export async function hasSufficientEthForGas(address, requiredEth = 0, bufferEth = DEFAULT_BUFFER_ETH) {
  if (!address) return false;
  const balance = await readEthBalance(address);
  const needed = parseFloat(requiredEth || 0) + bufferEth;
  if (!Number.isFinite(balance) || !Number.isFinite(needed)) return false;
  return balance >= needed;
}

export async function assertGasOrDrip(walletAddress, opts) {
  const ok = await ensureGasOrDrip(walletAddress, opts);
  if (!ok) throwGasDripBlocked();
  return true;
}

function normalizePlayType(labelOrType) {
  const t = String(labelOrType || 'market').toLowerCase();
  if (t === 'free' || t === 'boost' || t === 'market') return t;
  if (t.includes('boost')) return 'boost';
  if (t.includes('free') || t.includes('jackpot')) return 'free';
  return 'market';
}

function denyGasWithModal(showNotification, message, modalOpts, notifyType = 'warning') {
  showNotification?.(message, notifyType, notifyType === 'warning' ? 10000 : 8000);
  showRelayerOutOfGasModal(message, modalOpts);
  return false;
}

/**
 * Check wallet Base ETH first. Only request a drip when the wallet is short
 * (or the balance cannot be read). Cooldown / cap / deny must never proceed
 * to a wallet confirm — callers must abort when this returns false.
 *
 * @param {string} walletAddress
 * @param {{
 *   label?: string,
 *   playType?: 'free'|'boost'|'market',
 *   showNotification?: Function,
 *   requiredEth?: number,
 *   userId?: string,
 *   claimAmountUsdc?: number,
 * }} [opts]
 */
export async function ensureGasOrDrip(
  walletAddress,
  { label, playType, showNotification, requiredEth = 0, userId, claimAmountUsdc } = {}
) {
  if (!walletAddress) return true;
  const type = normalizePlayType(playType || label);
  const uid = userId || getAuthUserId();
  if (!uid) {
    return denyGasWithModal(
      showNotification,
      'You must be logged in as a valid platform user to use gas drip. Fund a little Base ETH to continue without the relayer.',
      { title: 'Login required for gas drip', playType: type }
    );
  }

  const needed = parseFloat(requiredEth || 0) + DEFAULT_BUFFER_ETH;
  let currentBalance = Number.NaN;
  try {
    currentBalance = await readEthBalance(walletAddress);
  } catch {
    currentBalance = Number.NaN;
  }

  // Enough ETH already — do not show a drip modal and do not hit the relayer.
  if (Number.isFinite(currentBalance) && currentBalance >= needed) return true;

  try {
    // Pre-check min jackpot / claim amount before hitting drip (better UX modal).
    let status = null;
    if (type === 'free') {
      try {
        const { data } = await api.get(`/relayer/users/${uid}/status`);
        status = data;
      } catch {
        /* server will enforce */
      }
    }

    const minJackpot = Number(status?.minJackpotUsdForDrip);
    const claimable = Number(status?.claimableJackpotUsd);
    const claimAmt = claimAmountUsdc != null ? Number(claimAmountUsdc) : null;

    if (type === 'free' && Number.isFinite(minJackpot) && minJackpot > 0) {
      const belowHeld =
        Number.isFinite(claimable) && claimable + 1e-9 < minJackpot;
      const belowClaim =
        claimAmt != null && Number.isFinite(claimAmt) && claimAmt + 1e-9 < minJackpot;
      if (belowHeld || belowClaim) {
        const msg = belowClaim
          ? `This claim is under $${minJackpot.toFixed(2)}. Fund Base ETH to claim smaller amounts, or accumulate at least $${minJackpot.toFixed(2)} Free jackpot to use platform gas drip.`
          : JACKPOT_MIN_MESSAGE;
        return denyGasWithModal(showNotification, msg, {
          title: 'Minimum Free jackpot required',
          playType: type,
          minJackpotUsd: minJackpot,
          claimableJackpotUsd: Number.isFinite(claimable) ? claimable : undefined,
        });
      }
    }

    try {
      const { data } = await api.post(`/relayer/users/${uid}/gasdrip`, {
        userId: uid,
        walletAddress,
        playType: type,
        label: label || type,
        claimAmountUsdc: claimAmt,
      });
      if (data?.sent) {
        showNotification?.('Gas funded for you — confirm in your wallet when prompted.', 'success');
      } else if (data?.ok && data?.sent === false) {
        // Backend already checked on-chain ETH and found enough. No modal.
        return true;
      } else {
        const after = await readEthBalance(walletAddress);
        if (Number.isFinite(after) && after >= needed) return true;
        return denyGasWithModal(
          showNotification,
          'Need a little Base ETH to pay gas. Fund your wallet on Base, then try again.',
          { title: 'Base ETH required', playType: type }
        );
      }
    } catch (e) {
      if (isDripCooldownError(e)) {
        const code = e?.response?.data?.code;
        const msg =
          e?.response?.data?.message ||
          'Gas drip is not available right now. Fund Base ETH to continue.';
        const nextEligibleAt = e?.response?.data?.nextEligibleAt;
        const responseType = normalizePlayType(e?.response?.data?.playType || type);
        const typeLabel =
          responseType === 'free' ? 'Free jackpot' : responseType === 'boost' ? 'Boost' : 'Market';
        const title =
          code === 'JACKPOT_MIN_NOT_MET'
            ? 'Minimum Free jackpot required'
            : code === 'ACCOUNT_TOO_NEW'
              ? 'Account too new'
              : code === 'DRIP_DAILY_CAP'
                ? `${typeLabel} gas drip limit reached`
                : code === 'DRIP_DISABLED'
                  ? `${typeLabel} gas drip disabled`
                  : code === 'PRIMARY_WALLET_ONLY'
                    ? 'Primary wallet required'
                    : code === 'USER_MISMATCH' || code === 'RELAYER_PATH_DEPRECATED'
                      ? 'Relayer access denied'
                      : code === 'DRIP_BUSY'
                        ? `${typeLabel} gas drip in progress`
                        : `${typeLabel} gas drip on cooldown`;
        return denyGasWithModal(showNotification, msg, {
          title,
          playType: responseType,
          nextEligibleAt,
          minJackpotUsd: e?.response?.data?.minJackpotUsd,
          claimableJackpotUsd: e?.response?.data?.claimableJackpotUsd,
        });
      }
      if (isRelayerOutOfGasError(e)) {
        const msg = e?.response?.data?.message || RELAYER_OUT_OF_GAS_MESSAGE;
        return denyGasWithModal(showNotification, msg, { playType: type }, 'error');
      }
      const typeLabel = type === 'free' ? 'Free jackpot' : type === 'boost' ? 'Boost' : 'Market';
      const msg =
        e?.response?.data?.message ||
        `Unable to fund gas${label ? ` for ${label}` : ''}. Please add a little Base ETH or try again.`;
      return denyGasWithModal(
        showNotification,
        msg,
        { title: `${typeLabel} gas funding unavailable`, playType: type },
        'error'
      );
    }

    for (let i = 0; i < POLL_ATTEMPTS; i += 1) {
      // eslint-disable-next-line no-await-in-loop
      await new Promise((r) => setTimeout(r, POLL_MS));
      // eslint-disable-next-line no-await-in-loop
      const ok = await hasSufficientEthForGas(walletAddress, requiredEth);
      if (ok) return true;
    }

    showNotification?.('Gas was funded but has not arrived yet. Please try again in a moment.', 'warning');
    return false;
  } catch {
    // Unknown failure after we already know ETH is missing or unreadable.
    // Never treat this as permission to send a transaction.
    return denyGasWithModal(
      showNotification,
      'Need a little Base ETH to pay gas. Fund your wallet on Base, then try again.',
      { title: 'Base ETH required', playType: type }
    );
  }
}
